import React, { useEffect, useState } from 'react';
import Typography from '../typography';
import { FlexBox } from '../common/wrappers';
import { useLocation, useNavigate } from 'react-router-dom';

import { routeNames } from '../../constants/routesPath';
import SampleOrderModal from '../sample-order';
import ConfirmationModal from '../common/confirmation-modal';
import SuccessSampleOrderModal from '../sample-order/successSampleOrderModal';
import Button from '../common/button';
import { useDispatch } from 'react-redux';
import amplitude from 'amplitude-js';
import { resetState } from '../../store/slices/sampleOrderSlice';
import { useAppSelector } from '../../store/hooks';
import { getUserData } from '../../store/slices/authSlice';
import { ReactComponent as QuestionMarkIcon } from '../../assets/images/questionMark.svg';
import CustomToolTip from '../custom-tooltip';

interface HeaderWithButtonProps {
  title: string;
}

const HeaderWithSampleButton: React.FC<HeaderWithButtonProps> = ({ title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const showSampleOrderButton =
    location.pathname === routeNames.myProducts ||
    location.pathname === routeNames.catalog ||
    location.pathname === routeNames.orders;

  const { userInfo } = useAppSelector(getUserData);
  const [showSampleOrder, setShowSampleOrder] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [showSuccessSampleOrder, setShowSuccessSampleOrder] =
    useState<boolean>(false);
  const [personalizedSampleOrderId, setPersonalizedSampleOrderId] = useState<
    string | null
  >(null);
  const [generatePayentMethodId, setGeneratePaymentMethodId] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const { pathname } = useLocation();
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  useEffect(() => {
    if (pathname.includes('orders')) {
      setIsPageLoaded(true);
    }
  }, [pathname]);

  useEffect(() => {
    const modalFromCatalog = localStorage.getItem('modalFromCatalog');
    if (modalFromCatalog && isPageLoaded) {
      setShowSampleOrder(true);
      if (modalFromCatalog) {
        /** the amplitude is lossing the context when its navigating from catlog to orders page because of that I have reinitialized the amplitude */
        amplitude
          .getInstance()
          .init(
            process.env.REACT_APP_AMPLITUDE_KEY as string,
            userInfo.everbeeProdUserId,
          );
        amplitude
          .getInstance()
          .logEvent('Sample Order - Initiated', { from: pathname });
      }
    }
  }, [isPageLoaded, pathname, userInfo.everbeeProdUserId]);
  const handleSampleModalClose = () => {
    setGeneratePaymentMethodId(false);
    setShowSampleOrder(false);
    dispatch(resetState());
    setShowCancelModal(false);
    localStorage.removeItem('modalFromCatalog');
  };

  const handleButtonClick = () => {
    if (!pathname.includes('/my-products') && !pathname.includes('/orders')) {
      navigate(routeNames.orders);
      localStorage.setItem('modalFromCatalog', 'true');
    } else {
      setShowSampleOrder(true);
      amplitude
        .getInstance()
        .logEvent('Sample Order - Initiated', { from: pathname });
    }
  };

  return (
    <>
      {showSampleOrderButton && (
        <FlexBox
          alignItems="center"
          justifyContent="space-between"
          style={{ padding: '20px 2rem 0 2rem' }}
        >
          <FlexBox alignItems="center" gap="0.5rem">
            <Typography fontSize="24px" fontWeight="600" text={title} />
            {title === 'My Products' && (
              <CustomToolTip
                title={'* indicates listing was published as Draft to Etsy.'}
                placement="top"
              >
                <QuestionMarkIcon />
              </CustomToolTip>
            )}
          </FlexBox>
          <Button
            label="Create Sample order"
            onClick={handleButtonClick}
            appearance="primary"
            size="large"
            className="sample-btn-catalog"
          />
        </FlexBox>
      )}
      {showSampleOrder && (
        <SampleOrderModal
          onClose={() => {
            localStorage.removeItem('modalFromCatalog');
            setShowSampleOrder(false);
          }}
          setShowSuccessSampleOrder={setShowSuccessSampleOrder}
          setShowCancelModal={setShowCancelModal}
          generatePayentMethodId={generatePayentMethodId}
          setGeneratePaymentMethodId={setGeneratePaymentMethodId}
          setPersonalizedSampleOrderId={setPersonalizedSampleOrderId}
        />
      )}
      {showSuccessSampleOrder && (
        <SuccessSampleOrderModal
          onClose={() => setShowSuccessSampleOrder(false)}
          personalizedSampleOrderId={personalizedSampleOrderId}
        />
      )}
      {showCancelModal && (
        <div className="sample-cancel-modal">
          <ConfirmationModal
            onClose={() => setShowCancelModal(false)}
            text={'All your progress will be lost'}
            onClick={handleSampleModalClose}
            headingText={'Warning'}
            buttonLabel={'Proceed'}
            buttonColor={'primary'}
          />
        </div>
      )}
    </>
  );
};

export default HeaderWithSampleButton;
